/**
 * ActivityFeed — the global, real-time audit stream (Activity tab). Derived from
 * the dashboard's live events + orders (no separate storage): event lifecycle,
 * order placements, and order edits — filterable, searchable, and exportable.
 */
import React, { useMemo, useState } from 'react';
import { activityMeta, timeAgo, formatDateTime, activityToCsv, downloadCsv } from '../eventUtils';
import { t } from '../strings';

function dayLabel(ms) {
  const d = new Date(ms);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
  const dd = new Date(d); dd.setHours(0, 0, 0, 0);
  if (dd.getTime() === today.getTime()) return 'Today';
  if (dd.getTime() === yesterday.getTime()) return 'Yesterday';
  return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

export default function ActivityFeed({ feed, events }) {
  const [kind, setKind] = useState('all');   // all | lifecycle | orders
  const [eventId, setEventId] = useState('');
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (feed || []).filter((e) => {
      if (kind === 'lifecycle' && e.kind !== 'lifecycle') return false;
      if (kind === 'orders' && e.kind === 'lifecycle') return false;
      if (eventId && e.eventId !== eventId) return false;
      if (q) {
        const hay = `${e.eventTitle} ${e.by} ${e.summary} ${activityMeta(e).label}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [feed, kind, eventId, search]);

  const groups = useMemo(() => {
    const m = new Map();
    for (const e of filtered) {
      const key = dayLabel(e.at);
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(e);
    }
    return [...m.entries()];
  }, [filtered]);

  const exportCsv = () => downloadCsv('event-activity.csv', activityToCsv(filtered));

  return (
    <div>
      <div className="ev-feedbar">
        <div className="ev-feedfilters">
          <button className={`ev-pillbtn${kind === 'all' ? ' is-on' : ''}`} onClick={() => setKind('all')}>{t('filterAll')}</button>
          <button className={`ev-pillbtn${kind === 'lifecycle' ? ' is-on' : ''}`} onClick={() => setKind('lifecycle')}>{t('filterEvents')}</button>
          <button className={`ev-pillbtn${kind === 'orders' ? ' is-on' : ''}`} onClick={() => setKind('orders')}>{t('filterOrders')}</button>
          <select className="ev-input ev-input--sm" value={eventId} onChange={(e) => setEventId(e.target.value)}>
            <option value="">{t('allEvents')}</option>
            {(events || []).map((ev) => <option key={ev.id} value={ev.id}>{ev.title || 'Untitled'}</option>)}
          </select>
        </div>
        <button className="ev__btn ev__btn--sm" onClick={exportCsv} disabled={!filtered.length}>⬇ {t('exportCsv')}</button>
      </div>

      <input className="ev-search" placeholder={t('searchActivity')} value={search} onChange={(e) => setSearch(e.target.value)} />

      {filtered.length === 0 ? (
        <div className="ev__empty">
          <div className="ev__empty-icon">🕘</div>
          <h3>{t('noActivity')}</h3>
          <p>Actions across your events and orders show up here automatically.</p>
        </div>
      ) : (
        <div className="ev-feed">
          {groups.map(([day, items]) => (
            <div key={day} className="ev-feed__group">
              <div className="ev-feed__day">{day}</div>
              {items.map((e) => {
                const meta = activityMeta(e);
                return (
                  <div key={e.id} className="ev-feed__row">
                    <span className="ev-feed__icon">{meta.icon}</span>
                    <div className="ev-feed__body">
                      <span className="ev-feed__label">
                        {meta.label}
                        {e.by ? <span className="ev-feed__by"> · {e.by}</span> : null}
                        {e.eventTitle ? <span className="ev-feed__event"> — {e.eventTitle}</span> : null}
                      </span>
                      {e.summary ? <span className="ev-feed__summary">{e.summary}</span> : null}
                    </div>
                    <span className="ev-feed__time" title={formatDateTime(e.at)}>{timeAgo(e.at)}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
